import React, { useEffect, useRef } from 'react';
import { Modal, Progress, Alert, Typography } from 'antd';
import { useQuery } from '@apollo/client';
import { GET_BILL_SUMMARY_PROGRESS } from '../../graphql/queries/settlement/billSummaryQueries';
import { BillSummaryProgress, BillSummaryTaskStatus } from '../../graphql/types/settlement/settlementBillTypes';
import { message } from 'antd';

const { Text } = Typography;

interface Props {
  visible: boolean;
  taskId: string | null;
  onClose: () => void;
  onCompleted: (success: boolean) => void;
}

/**
 * 账单汇总进度弹窗
 * 
 * 功能：
 * - 实时显示汇总任务进度（每2秒轮询）
 * - 成功时显示汇总结果并自动关闭
 * - 失败时显示错误信息
 */
export const BillSummaryProgressModal: React.FC<Props> = ({
  visible,
  taskId,
  onClose,
  onCompleted
}) => {
  // 使用 ref 记录是否已经处理过完成状态，防止重复显示消息
  const hasCompletedRef = useRef(false);

  // 轮询查询进度（每2秒）
  const { data, stopPolling } = useQuery<{
    getBillSummaryProgress: BillSummaryProgress;
  }>(GET_BILL_SUMMARY_PROGRESS, {
    variables: { taskId },
    skip: !taskId || !visible,
    pollInterval: 2000,
    fetchPolicy: 'network-only'
  });

  // 当弹窗重新打开时，重置完成标志
  useEffect(() => {
    if (visible) {
      hasCompletedRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (data?.getBillSummaryProgress && !hasCompletedRef.current) {
      const progress = data.getBillSummaryProgress;
      
      if (progress.status === BillSummaryTaskStatus.SUCCESS) {
        // 汇总成功
        hasCompletedRef.current = true; // 标记为已处理
        stopPolling();
        
        const summaryCount = progress.summaryCount || 0;
        const totalAmount = progress.totalAmount || 0;
        const amountFormatted = `$ ${(totalAmount / 100).toFixed(2)}`;
        
        message.success(
          `汇总成功！汇总了 ${summaryCount} 个费用项，总金额：${amountFormatted}`,
          5
        );
        
        onCompleted(true);
        setTimeout(() => {
          onClose();
        }, 500);
      } else if (progress.status === BillSummaryTaskStatus.FAILED) {
        // 汇总失败
        hasCompletedRef.current = true; // 标记为已处理
        stopPolling();
        message.error(
          `汇总失败: ${progress.errorMessage || '未知错误'}`,
          5
        );
        onCompleted(false);
      }
    }
  }, [data, stopPolling, onCompleted, onClose]);

  // 清理：当弹窗关闭时停止轮询
  useEffect(() => {
    if (!visible) {
      stopPolling();
    }
  }, [visible, stopPolling]);

  const progress = data?.getBillSummaryProgress;

  // 获取进度条状态
  const getProgressStatus = () => {
    if (!progress) return 'active';
    if (progress.status === BillSummaryTaskStatus.SUCCESS) return 'success';
    if (progress.status === BillSummaryTaskStatus.FAILED) return 'exception';
    return 'active';
  };

  // 获取进度条颜色
  const getStrokeColor = () => {
    if (progress?.status === BillSummaryTaskStatus.SUCCESS) {
      return '#52c41a';
    }
    return undefined;
  };

  // 允许用户随时关闭弹窗（任务在后台继续执行）
  const handleClose = () => {
    if (progress?.status === BillSummaryTaskStatus.RUNNING) {
      message.info('任务将在后台继续执行，完成后会自动刷新列表');
    }
    onClose();
  };

  return (
    <Modal
      title="账单汇总进度"
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={true}
      maskClosable={false}
      width={500}
    >
      <div style={{ padding: '20px 0' }}>
        <Progress 
          percent={progress?.progress || 0} 
          status={getProgressStatus()}
          strokeColor={getStrokeColor()}
        />
        
        <div style={{ 
          marginTop: 16, 
          textAlign: 'center', 
          minHeight: 24
        }}>
          <Text type="secondary">
            {progress?.currentStep || '任务已创建，等待处理...'}
          </Text>
        </div>
        
        {progress?.errorMessage && (
          <Alert
            message="错误信息"
            description={progress.errorMessage}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        
        {(!progress || progress.status === BillSummaryTaskStatus.PENDING) && (
          <Alert
            message="任务已创建"
            description="系统将在 1 分钟内开始处理此任务，请耐心等待..."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        
        {progress?.status === BillSummaryTaskStatus.RUNNING && (
          <div style={{ 
            marginTop: 16, 
            textAlign: 'center',
            fontSize: 12,
            color: '#999'
          }}>
            正在处理中，请稍候...
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BillSummaryProgressModal;

